import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import AOS from 'aos';
import { FaArrowRight, FaCheckCircle, FaGraduationCap, FaLaptopCode, FaBriefcase, FaUsers, FaStar, FaPlay } from 'react-icons/fa';

const PROGRAMS = [
  { title: 'Full-Stack Web Development', duration: '6 Months', tech: 'React, Node.js, MongoDB, AWS', level: 'Beginner to Advanced' },
  { title: 'Data Science & AI/ML', duration: '4 Months', tech: 'Python, TensorFlow, Pandas, SQL', level: 'Intermediate' },
  { title: 'DevOps & Cloud Engineering', duration: '3 Months', tech: 'AWS, Docker, Kubernetes, CI/CD', level: 'Intermediate' },
  { title: 'Mobile App Development', duration: '4 Months', tech: 'Flutter, React Native, Firebase', level: 'Beginner to Advanced' },
];

const OUTCOMES = [
  { icon: <FaLaptopCode />, value: '100%', label: 'Placement Assistance', color: '#1e40af' },
  { icon: <FaUsers />, value: '33+', label: 'Placed Professionals', color: '#0891b2' },
  { icon: <FaBriefcase />, value: '50+', label: 'Hiring Partners', color: '#7c3aed' },
  { icon: <FaStar />, value: '4.9/5', label: 'Student Satisfaction', color: '#d97706' },
];

const TalentX = () => {
  useEffect(() => { AOS.init({ duration: 900, once: true, offset: 80 }); }, []);

  return (
    <div style={{ background: 'var(--color-bg)' }}>
      <section className="page-hero">
        <div className="page-hero-overlay" />
        <Container>
          <div className="page-hero-content" data-aos="fade-up">
            <div className="breadcrumb-vk"><Link to="/">Home</Link> / <span>TalentX</span></div>
            <h1 className="page-hero-title">TalentX — <span>Tech Training & Placement</span></h1>
            <p className="page-hero-desc">
              Industry-aligned training programs with live project experience and 100% placement support. We bridge the gap between education and employment in the tech industry.
            </p>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.75rem', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn-primary-vk">Enroll Now <FaArrowRight /></Link>
              <Link to="/contact" className="btn-outline-vk">See Programs <FaPlay /></Link>
            </div>
          </div>
        </Container>
      </section>

      {/* Outcomes */}
      <section style={{ padding: '3rem 0', background: 'var(--color-bg)', borderBottom: '1px solid var(--color-border)' }}>
        <Container>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '1rem' }}>
            {OUTCOMES.map((o, i) => (
              <div key={i} style={{ textAlign: 'center', padding: '1.5rem' }} data-aos="fade-up" data-aos-delay={i * 80}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: `${o.color}12`, color: o.color, display: 'grid', placeItems: 'center', fontSize: '1.25rem', margin: '0 auto 0.75rem' }}>{o.icon}</div>
                <div style={{ fontSize: '1.875rem', fontWeight: 900, background: 'var(--gradient-primary)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{o.value}</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', fontWeight: 500, marginTop: '0.25rem' }}>{o.label}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Programs */}
      <section className="section-py" style={{ background: 'var(--color-bg-alt)' }}>
        <Container>
          <div className="section-header text-center" data-aos="fade-up">
            <span className="section-badge">Training Programs</span>
            <h2 className="section-title">Industry-Ready <span>Courses</span></h2>
            <p className="section-subtitle">Live projects, expert mentors, and guaranteed placement support.</p>
          </div>
          <Row className="g-4">
            {PROGRAMS.map((p, i) => (
              <Col md={6} key={i} data-aos="fade-up" data-aos-delay={i * 100}>
                <div className="vk-card" style={{ height: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(8,145,178,0.1)', color: '#0891b2', display: 'grid', placeItems: 'center', fontSize: '1.2rem' }}><FaGraduationCap /></div>
                    <span style={{ fontSize: '0.8125rem', background: 'rgba(30,64,175,0.08)', color: 'var(--color-primary)', border: '1px solid rgba(30,64,175,0.15)', padding: '3px 10px', borderRadius: 999, fontWeight: 600 }}>{p.duration}</span>
                  </div>
                  <h4 style={{ fontSize: '1.0625rem', fontWeight: 700, color: 'var(--color-text)', marginBottom: '0.5rem' }}>{p.title}</h4>
                  <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}><strong>Tech:</strong> {p.tech}</p>
                  <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginBottom: '1.25rem' }}><strong>Level:</strong> {p.level}</p>
                  {['Live project training', 'Expert mentorship', 'Placement assistance', 'Industry certification'].map((f, fi) => (
                    <div key={fi} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: 'var(--color-text-muted)', marginBottom: 5 }}>
                      <FaCheckCircle style={{ color: '#059669', fontSize: '0.75rem', flexShrink: 0 }} /> {f}
                    </div>
                  ))}
                  <Link to="/contact" className="btn-secondary-vk" style={{ marginTop: '1.25rem', justifyContent: 'center' }}>Apply Now <FaArrowRight /></Link>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="cta-section">
        <Container className="text-center" data-aos="zoom-in">
          <h2 className="cta-title">Launch Your <span>Tech Career Today</span></h2>
          <p className="cta-desc">Join 33+ professionals who launched their tech careers through TalentX.</p>
          <Link to="/contact" className="btn-primary-vk">Enroll Now <FaArrowRight /></Link>
        </Container>
      </section>
    </div>
  );
};

export default TalentX;
